import { z } from 'zod';

/**
 * Shared pieces of the Smart Search filter specs (meters, customers, ...).
 *
 * Each entity declares its own filter fields and sort columns, spreads
 * `pagingFields(...)` into its object schema, and lists its fields in a
 * `SearchFieldKinds` map (in URL order) for the codec below.
 *
 * Paging and sort use the same keys and conventions as `useTableUrlState`
 * (`page` 1-indexed, `pageSize`, `sortBy`, `sortDir`), so a results DataTable
 * can own those params directly.
 */

export const SEARCH_DEFAULT_PAGE_SIZE = 50;
export const SEARCH_MAX_PAGE_SIZE = 100;
export const SEARCH_MAX_PAGE = 10_000;
export const SEARCH_MAX_TEXT_LENGTH = 200;

// Upper bounds keep every value inside what Postgres will accept for its
// column or parameter type, so out-of-range input fails validation (400)
// instead of reaching the database and failing there (500). The int4 cap is
// a Postgres limit; the page and text caps are product choices (an offset past
// any customer's meter count, a search phrase) and can be raised if a real
// query needs more.
export const INT4_MAX = 2_147_483_647;
export const searchId = z.number().int().positive().max(INT4_MAX);
/** Non-negative whole-number threshold (counts), bounded to int4. */
export const searchCount = z.number().int().nonnegative().max(INT4_MAX);
export const searchText = z.string().trim().min(1).max(SEARCH_MAX_TEXT_LENGTH);
/**
 * Non-negative USD amount threshold. The cap is a product choice (well above
 * any customer's annual revenue) that keeps the value an exact float8 bind.
 */
export const searchMoney = z.number().nonnegative().max(1_000_000_000);

/** `page` / `pageSize` / `sortBy` / `sortDir`, with the shared defaults and bounds. */
export function pagingFields<const T extends readonly [string, ...string[]]>(sortColumns: T) {
  return {
    page: z.number().int().positive().max(SEARCH_MAX_PAGE).default(1)
      .describe('1-indexed page number'),
    pageSize: z.number().int().positive().max(SEARCH_MAX_PAGE_SIZE)
      .default(SEARCH_DEFAULT_PAGE_SIZE).describe('Results per page'),
    sortBy: z.enum(sortColumns).optional().describe('Column to sort by'),
    sortDir: z.enum(['asc', 'desc']).default('asc').describe('Sort direction'),
  };
}

/** How each URL param is read back: numbers and booleans are converted, strings kept. */
export type SearchFieldKinds = Record<string, 'number' | 'string' | 'boolean'>;

/**
 * Serialize a search to URL params, in `fields` order. Defaults (page 1, the
 * default page size, ascending sort) are omitted so a plain search URL stays
 * short, matching `useTableUrlState`.
 */
export function toSearchParams(
  search: Record<string, unknown>,
  fields: SearchFieldKinds,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of Object.keys(fields)) {
    const value = search[key];
    if (value === undefined) continue;
    if (key === 'page' && value === 1) continue;
    if (key === 'pageSize' && value === SEARCH_DEFAULT_PAGE_SIZE) continue;
    if (key === 'sortDir' && value === 'asc') continue;
    params.set(key, String(value));
  }
  return params;
}

/**
 * Parse URL params into a validated search. Only keys in `fields` are read, so
 * the spec can share a URL with other page state; empty values count as
 * absent. Malformed values fail validation rather than being coerced or
 * dropped.
 */
export function parseSearchParams<S extends z.ZodType>(
  schema: S,
  fields: SearchFieldKinds,
  params: URLSearchParams,
): ReturnType<S['safeParse']> {
  const raw: Record<string, string | number | boolean> = {};
  for (const [key, kind] of Object.entries(fields)) {
    const value = params.get(key);
    if (!value) continue;
    // ponytail: Number('abc') is NaN and 'yes' stays a string; the schema rejects both.
    if (kind === 'number') raw[key] = Number(value);
    else if (kind === 'boolean') {
      raw[key] = value === 'true' ? true : value === 'false' ? false : value;
    } else raw[key] = value;
  }
  return schema.safeParse(raw) as ReturnType<S['safeParse']>;
}
